import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import scrollSelectorIntoView from 'Helpers/ScrollSelectorIntoView';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useUserContext from 'Context/UserContext';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { IconButton, ListItemAvatar, ListItemSecondaryAction } from '@material-ui/core';
import { Tooltip } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import useHomeContext from 'Context/HomeContext';
import useEventTrackingLogEvent, { eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import NavItem from './index';
import { isHidden } from '../utils';

const styles = theme => ({
  root: {
    borderLeft: '3px solid',
    borderLeftColor: 'transparent',
    '&.Mui-selected': {
      borderLeft: '3px solid',
      borderLeftColor: theme.palette.facet.primaryBlue,
      color: theme.palette.facet.primaryBlue,
      marginLeft: 0,
    },
    '&:hover': {
      color: theme.palette.facet.primaryBlue,
    },
    '&:hover $icon': {
      color: theme.palette.facet.primaryBlue,
    },
  },
  mobileLinkItem: {
    textAlign: 'center',
    width: '100vw',
    paddingLeft: '50px',
    color: theme.palette.grey[500],
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.facet.primaryGray3,
    '&.Mui-selected': {
      borderBottom: 0,
      color: theme.palette.facet.primaryBlue,
      backgroundColor: lighten(theme.palette.facet.accentBlue, 0.90),
    },
  },
  icon: {
    width: theme.spacing(2.2),
    color: theme.palette.facet.primaryGray3,
    '&:hover': {
      color: theme.palette.facet.primaryBlue,
    },
  },
  listItemIconRoot: {
    minWidth: theme.spacing(2.5),
    width: theme.spacing(4.5),
  },
  avatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  listItemAvatarRoot: {
    minWidth: theme.spacing(2.5),
    width: theme.spacing(4.5),
  },
  mobileIcon: {
    color: theme.palette.facet.primaryBlue,
  },
  selectedIcon: {
    color: theme.palette.facet.primaryBlue,
  },
  subLink: {
    color: theme.palette.facet.primaryGray3,
  },
  nested: {
    padding: theme.spacing(0.25, 1, 0.25, 6.5),
  },
  divider: {
    borderBottom: '1px solid',
    borderBottomColor: lighten(theme.palette.grey[300], 0.10),
  },
  mobileListOpen: {
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.grey[100],
    backgroundColor: lighten(theme.palette.facet.accentBlue, 0.90),
  },
  mobileNested: {
    padding: 0,
    textAlign: 'center',
    color: theme.palette.facet.primaryBlue,
  },
  nestedWithAction: {
    padding: theme.spacing(0.25, 4, 0.25, 9),
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  animatedItem: {
    animation: `$fadeIn 1000ms ${theme.transitions.easing.easeInOut}`,
  },
  secondaryIcon: {
    color: theme.palette.facet.primaryGray3,
    '&:hover': {
      color: theme.palette.facet.primaryBlue,
    },
  },
});

const DropdownItem = ({
  className,
  classes,
  disabled,
  icon,
  label,
  to,
  hash,
  subLinks,
  BadgeComponent,
  BadgeComponentProps,
  subComponents,
  drawerOpen,
  isMobileItem,
  onClick,
  avatar,
  avatarProps,
  test,
}) => {
  const [isOpen, setOpenState] = useState(false);
  const { householdResource } = useSimpleHousehold();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { homeResource } = useHomeContext();
  const {
    hasSuperUserRole, isInvestmentOps, userResource, agentResource,
  } = useUserContext();
  const history = useHistory();
  const selected = to === history.location.pathname;
  const open = isMobileItem ? isOpen : (isOpen && drawerOpen);
  const Icon = icon;
  const Avatar = avatar;

  useEffect(() => {
    if (!isMobileItem) {
      if (!Avatar && !Icon) {
        throw new Error('Avatar or Icon is required');
      } else if (Avatar) {
        if (!avatarProps) {
          throw new Error('avatarProps are required: {alt: "avatar description", src: "/path/to/avatar}');
        }
      }
    }
  }, [Avatar, Icon]);

  const renderTo = () => {
    let path;
    if (typeof to === 'function') {
      path = to({ householdResource, userResource, agentResource });
    } else {
      path = to;
    }

    if (hash) {
      path = `${path}#${hash}`;
    }

    return path;
  };

  const onNavItemClick = (subLink) => {
    if (subLink.logEvent) {
      logStandardizedEvent(subLink.eventLoggingCategory,
        eventTypes.navigateTo,
        `${subLink.label} -  Navigated to ${subLink.label} via Dropdown`);
    }
    if (onClick) onClick();
    if (isMobileItem) setOpenState(false);
    if (subLink.hash) scrollSelectorIntoView(`#${subLink.hash}`);
  };

  return (
    <>
      <ListItem
        button
        disabled={disabled}
        selected={selected}
        component={to ? Link : null}
        to={renderTo()}
        test={test}
        onClick={() => {
          if (onClick) onClick();
          if (!to) setOpenState(!isOpen);
        }}
        className={classNames(className, {
          [classes.root]: !isMobileItem,
          [classes.divider]: !isOpen && !isMobileItem,
          [classes.mobileLinkItem]: isMobileItem,
        })}
      >
        {!isMobileItem && Icon && (
        <Tooltip title={label}>
          <ListItemIcon
            classes={{
              root: classes.listItemIconRoot,
            }}
          >
            <BadgeComponent {...BadgeComponentProps}>
              <Icon
                className={classNames(classes.icon, {
                  [classes.selectedIcon]: selected,
                })}
              />
            </BadgeComponent>
          </ListItemIcon>
        </Tooltip>
        )}
        {!isMobileItem && Avatar && (
          <ListItemAvatar classes={{ root: classes.listItemAvatarRoot }}>
            <Avatar {...avatarProps} className={classes.avatar} />
          </ListItemAvatar>)
        }
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            variant: isMobileItem ? 'h2' : 'h6',
            noWrap: true,
          }}
        />
        {(drawerOpen || isMobileItem) && (
          <ListItemSecondaryAction
            className={classNames({
              [classes.animatedItem]: drawerOpen,
            })}
          >
            <IconButton
              edge="end"
              aria-label={isOpen ? 'close' : 'open'}
              className={classNames({
                [classes.secondaryIcon]: !isMobileItem,
                [classes.mobileIcon]: isMobileItem,
              })}
              onClick={() => setOpenState(!isOpen)}
              test={`${test}-dropdown-button`}
            >
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </ListItemSecondaryAction>)
        }
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          component="div"
          className={classNames({
            [classes.divider]: isOpen && !isMobileItem,
            [classes.mobileListOpen]: isOpen && isMobileItem,
          })}
        >
          {subLinks && subLinks.filter(isHidden({
            householdResource,
            userResource,
            hasSuperUserRole,
            isInvestmentOps,
            agentResource,
            homeResource,
          })).map((subLink) => {
            return (
              <NavItem
                key={subLink.key}
                isSubLink
                className={classNames(classes.subLink, {
                  [classes.nested]: !isMobileItem,
                  [classes.mobileNested]: isMobileItem,
                  [classes.nestedWithAction]: !isMobileItem && subLink.secondaryAction,
                })}
                onClick={() => onNavItemClick(subLink)}
                isMobileItem={isMobileItem}
                {...subLink}
              />);
          })}
          {subComponents && subComponents.map((Component, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Component key={`component-${i}`} />
          ))}
        </List>
      </Collapse>
    </>
  );
};

DropdownItem.defaultProps = {
  className: '',
  disabled: false,
  isMobileItem: false,
  onClick: null,
  drawerOpen: false,
  avatar: null,
  avatarProps: {},
  to: null,
  icon: null,
  iconProps: {},
  test: '',
  BadgeComponent: React.Fragment,
};

DropdownItem.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  icon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
  ]),
  avatar: PropTypes.object,
  avatarProps: PropTypes.object,
  label: PropTypes.string.isRequired,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  isMobileItem: PropTypes.bool,
  onClick: PropTypes.func,
  drawerOpen: PropTypes.bool,
  test: PropTypes.string,
  BadgeComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.symbol]),
};

export default withStyles(styles)(DropdownItem);
